import { useCallback, useEffect } from 'react'
import { setSessionIdInSessionStorage } from '../../utils/sessionStorageUtil'
import { buildRelativeLandingPageUrl } from '../../utils/UrlBuilders'
import { TextId, useTextsV2 } from '../../utils/useTexts'
import { GoFmMode } from '../../api/goFmMode'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { getUrlParametersToString } from '../../utils/getUrlParametersToString'
import {
  getFactoryModelsMode,
  getMarketLanguageState,
  getMarketSettingsState,
} from '../../store/appSlice'
import {
  getIsLoggedInState,
  getIsLoadedTruck,
  getSessionInitDataState,
  getUserSelectionTimestamp,
} from '../../store/sessionDataSlice'
import { closeAllModals, closeModalChangesLost } from '../../store/modalSlice'
import { TdsButton, TdsModal } from '@scania/tegel-react'
import { openGvPanel, setGvSidePanelContent } from '../../store/sidePanelSlice'
import { SidePanelContent } from '../../store/types'
import { convertReduxStateToTruckInfo } from '../../App'
import { getTruckInfoState } from '../../store/truckInfoSlice'

interface ModalChangesLostProps {
  handleSaveAsClick: (e: React.MouseEvent) => void
  handleSetSuppressNativePageReloadWarning: (suppress: boolean) => void
}

export function ModalChangesLost({
  handleSaveAsClick,
  handleSetSuppressNativePageReloadWarning,
}: ModalChangesLostProps): JSX.Element {
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const t2 = useTextsV2()
  const dispatch = useAppDispatch()
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const sessionId = sessionInitData?.sessionId
  const marketSettings = useAppSelector(getMarketSettingsState)
  const factoryModelsMode = useAppSelector(getFactoryModelsMode)
  const isLoggedIn = useAppSelector(getIsLoggedInState)
  const isLoadedTruck = useAppSelector(getIsLoadedTruck)
  const truckInfo = useAppSelector(getTruckInfoState)
  const truckInfoDate = convertReduxStateToTruckInfo(truckInfo)
  const userSelectionTimestamp = useAppSelector(getUserSelectionTimestamp)
  const saveEnabled =
    isLoadedTruck &&
    userSelectionTimestamp?.getTime() === truckInfoDate?.timeLoaded.getTime()

  const handleDiscardClick = useCallback(() => {
    if (!marketLanguage) {
      return
    }
    if (sessionId && isLoggedIn) {
      setSessionIdInSessionStorage(sessionId)
    }
    // Reload the application from the landing page.
    const relativeUrl = buildRelativeLandingPageUrl(marketLanguage)
    const params = relativeUrl.params
    document.location.href = relativeUrl.path + getUrlParametersToString(params)
  }, [marketLanguage, sessionId, isLoggedIn])

  const handleSaveClick = useCallback(() => {
    dispatch(closeModalChangesLost())
    if (isLoggedIn) {
      dispatch(openGvPanel())
      dispatch(setGvSidePanelContent(SidePanelContent.save))
    }
    // TODO - redirect to Save after login
    if (!isLoggedIn) {
      dispatch(openGvPanel())
      dispatch(setGvSidePanelContent(SidePanelContent.login))
    }
  }, [dispatch, isLoggedIn])

  const handleCloseClick = useCallback(() => {
    dispatch(closeAllModals())
    handleSetSuppressNativePageReloadWarning(false)
  }, [dispatch, handleSetSuppressNativePageReloadWarning])

  // Eventlistener to close modal on tdsClose (click outside modal and click on close cross)
  useEffect(() => {
    document.addEventListener('tdsClose', handleCloseClick)
    return () => {
      document.removeEventListener('tdsClose', handleCloseClick)
    }
  }, [handleCloseClick])

  return (
    <TdsModal size="md" header={t2(TextId.HEADER_LOSING_CHANGES)} show>
      <span slot="body">
        <div>{t2(TextId.DESCRIPTION_LOSING_CHANGES)}</div>
      </span>
      <span slot="actions" className="tds-u-flex tds-u-gap2 tds-u-flex-wrap">
        {marketSettings?.garageEnabled &&
          factoryModelsMode !== GoFmMode.OnlyFactoryModels && (
            <TdsButton
              variant="primary"
              size="md"
              text={t2(TextId.LABEL_ACTION_SAVE_AS)}
              onClick={(e) => handleSaveAsClick(e)}
            />
          )}
        {saveEnabled && (
          <TdsButton
            variant="primary"
            size="md"
            text={t2(TextId.LABEL_ACTION_SAVE)}
            onClick={handleSaveClick}
          />
        )}
        <TdsButton
          variant="primary"
          size="md"
          text={t2(TextId.LABEL_ACTION_DISCARD_CHANGES)}
          onClick={handleDiscardClick}
        />
        <TdsButton
          variant="secondary"
          size="md"
          text={t2(TextId.LABEL_ACTION_CLOSE)}
          onClick={handleCloseClick}
        />
      </span>
    </TdsModal>
  )
}
